import { subMonths } from 'date-fns';
import TimeStr from './TimeStr';

class MyDate {
  year: number;
  month: number;
  day: number;

  constructor(date?: Date) {
    const d = date || new Date();
    this.year = d.getFullYear();
    this.month = d.getMonth() + 1;
    this.day = d.getDate();
  }

  static fromYMD(y: number, m: number, d: number) {
    return this.fromNumber(y * 10000 + m * 100 + d);
  }

  static fromYearMonthWithDay(ym: YearMonth, d: number) {
    return this.fromNumber(ym.toNumber() * 100 + d);
  }

  static fromYMDWithCheck(y: number, m: number, d: number): MyDate | null {
    if (m < 1 || m > 12) return null;

    if (d < 1 || d > new YearMonth(y, m).getDays()) return null;

    return this.fromNumber(y * 10000 + m * 100 + d);
  }

  static fromStr(str: string) {
    if (str.length !== 8) throw new Error('invalid date format');

    const dateStr =
      `${str.substring(0, 4)}` + `-${str.substring(4, 6)}` + `-${str.substring(6, 8)}`;
    const utcNoonDate = new Date(`${dateStr}T12:00:00Z`);
    const date = new Date(utcNoonDate);
    return new MyDate(date);
  }

  static fromNumber(n: number) {
    // console.log('MyDate fromNumber', n);
    return this.fromStr(n.toString());
  }

  toDate() {
    const dateStr =
      `${this.year}` +
      `-${this.month.toString().padStart(2, '0')}` +
      `-${this.day.toString().padStart(2, '0')}`;

    const date = new Date(`${dateStr}T12:00:00Z`);
    // console.log('toDate dateStr', dateStr);
    // console.log('toDate date', date);
    return date;
  }

  toNumber() {
    return this.year * 10000 + this.month * 100 + this.day;
  }

  toStr() {
    return this.toNumber().toString();
  }

  yearMonth() {
    return new YearMonth(this.year, this.month);
  }

  /** Returns the day of the week (0 for Sunday, 6 for Saturday) for the date represented by this instance. */
  dayOfWeek(): number {
    /*
    0: Sunday
    1: Monday
    2: Tuesday
    3: Wednesday
    4: Thursday
    5: Friday
    6: Saturday
    */

    const date = this.toDate();

    // Return the day of the week
    return date.getDay();
  }

  dayOfWeekKor(): string {
    // Array mapping days of the week to their Korean representation
    const daysInKorean = ['일', '월', '화', '수', '목', '금', '토'];

    // Get the day of the week as a number using dayOfWeek method
    const dayOfWeekNum = this.dayOfWeek();

    // Return the corresponding Korean day of the week
    return daysInKorean[dayOfWeekNum];
  }

  shortYear() {
    return this.year % 100;
  }

  toReadableDate(pattern: '-' = '-', detail: 'high' | 'semi-high' | 'medium' = 'medium'): string {
    return TimeStr.getReadableDate(this.toDate(), pattern, detail);
  }

  next(n = 1): MyDate {
    const nextDay = new Date(this.year, this.month - 1, this.day + n);
    return new MyDate(nextDay);
  }

  prev(n = 1): MyDate {
    const prevDay = new Date(this.year, this.month - 1, this.day - n);
    return new MyDate(prevDay);
  }
}

class YearMonth {
  year: number;
  month: number;

  constructor(y: number, m: number) {
    y = Math.floor(y);
    m = Math.floor(m);
    if (y < 1900 || y > 2100) throw new Error('invalid year');
    if (m < 1 || m > 12) throw new Error('invalid month');
    this.year = y;
    this.month = m;
  }

  static fromToday() {
    const today = new Date();
    return new YearMonth(today.getFullYear(), today.getMonth() + 1);
  }

  static fromNumber(n: number) {
    return new YearMonth(Math.floor(n / 100), n % 100);
  }

  toString = () => {
    return `${this.year}${this.month.toString().padStart(2, '0')}`;
  };

  toReadableString = () => {
    return `${this.year}-${this.month.toString().padStart(2, '0')}`;
  };

  toNumber() {
    return this.year * 100 + this.month;
  }

  next = () => {
    let y = this.year;
    let m = this.month + 1;
    if (m > 12) {
      m -= 12;
      y += 1;
    }
    return new YearMonth(y, m);
  };

  prev = () => {
    let y = this.year;
    let m = this.month - 1;
    if (m < 1) {
      m += 12;
      y -= 1;
    }
    return new YearMonth(y, m);
  };

  subtractMonths(n: number): YearMonth {
    const yearsToSubtract = Math.floor(n / 12);
    const monthsToSubtract = n % 12;

    let newYear = this.year - yearsToSubtract;
    let newMonth = this.month - monthsToSubtract;

    // If subtracting months crosses the year boundary
    if (newMonth <= 0) {
      newYear -= 1; // Adjust for the year decrease
      newMonth += 12; // Adjust month to proper value
    }

    return new YearMonth(newYear, newMonth);
  }

  getDays = () => {
    const y = this.year;
    const m = this.month;
    return m === 2 ? (y & 3 || (!(y % 25) && y & 15) ? 28 : 29) : 30 + ((m + (m >> 3)) & 1);
  };

  getFirstDay = () => {
    return MyDate.fromYMD(this.year, this.month, 1);
  };

  getLastDay = () => {
    return MyDate.fromYMD(this.year, this.month, this.getDays());
  };

  getFirstDayMidnight = (timeZoneOffsetInHour = 9): Date => {
    const localDate = new Date(Date.UTC(this.year, this.month - 1, 1));

    // Subtract the offset to get the UTC representation.
    const utcMilliseconds = localDate.getTime() - timeZoneOffsetInHour * 60 * 60 * 1000;
    return new Date(utcMilliseconds);
  };

  getLastDayMidnight = (timeZoneOffsetInHour = 9): Date => {
    const localDate = new Date(Date.UTC(this.year, this.month, 0)); // 0 gets the last day of the previous month, hence this.month is not decremented

    // Set hours to 23, minutes to 59, and seconds to 59
    localDate.setUTCHours(23, 59, 59, 999);

    // Subtract the offset to get the UTC representation
    const utcMilliseconds = localDate.getTime() - timeZoneOffsetInHour * 60 * 60 * 1000;
    return new Date(utcMilliseconds);
  };
}

class PeriodYM {
  from: YearMonth; // older
  to: YearMonth; // newer

  static regex = /(([0-9]{4})(((0)[1-9])|(1)[0-2])){2}/;

  constructor(from: YearMonth, to: YearMonth) {
    if (from.toNumber() < to.toNumber()) {
      this.from = from;
      this.to = to;
    } else {
      this.from = to;
      this.to = from;
    }
  }

  static fromString(str: string): PeriodYM | null {
    if (!this.regex.test(str)) return null;

    // yyyymmyyyymm 포맷을 substring(start, endIndex)로 분리한다.
    const fy = parseInt(str.substring(0, 4));
    const fm = parseInt(str.substring(4, 6));
    const ty = parseInt(str.substring(6, 10));
    const tm = parseInt(str.substring(10, 12));

    return new PeriodYM(new YearMonth(fy, fm), new YearMonth(ty, tm));
  }

  static monthTillNow(count: number): PeriodYM {
    const now = new Date();
    const old = subMonths(now, count);

    return new PeriodYM(
      new YearMonth(old.getFullYear(), old.getMonth() + 1),
      new YearMonth(now.getFullYear(), now.getMonth() + 1),
    );
  }

  toString = () => {
    return `${this.from.toString()}${this.to.toString()}`;
  };

  getYmNumbers = (): number[] => {
    const nums: number[] = [];
    for (let ym = this.from; ym.toNumber() <= this.to.toNumber(); ym = ym.next()) {
      nums.push(ym.toNumber());
    }
    return nums;
  };

  getMonthCount = (): number => {
    return (this.to.year - this.from.year) * 12 + (this.to.month - this.from.month) + 1;
  };
}

class MyTime {
  hour: number;
  minute: number;
  second: number;

  constructor(date?: Date) {
    const d = date || new Date();
    this.hour = d.getHours();
    this.minute = d.getMinutes();
    this.second = d.getSeconds();
  }

  static fromStr(str: string) {
    if (str.length !== 6) throw new Error('invalid time format');

    const h = parseInt(str.substring(0, 2));
    const m = parseInt(str.substring(2, 4));
    const s = parseInt(str.substring(4, 6));

    const date = new Date();
    date.setHours(h);
    date.setMinutes(m);
    date.setSeconds(s);

    return new MyTime(date);
  }

  static fromNumber(n: number) {
    return this.fromStr(n.toString().padStart(6, '0'));
  }

  toDate() {
    const date = new Date();
    date.setHours(this.hour);
    date.setMinutes(this.minute);
    date.setSeconds(this.second);
    return date;
  }

  toNumber() {
    return this.hour * 10000 + this.minute * 100 + this.second;
  }

  toStr() {
    return this.toNumber().toString().padStart(6, '0');
  }

  toShortNumber() {
    return this.hour * 100 + this.minute;
  }

  toShortStr() {
    return this.toShortNumber().toString().padStart(4, '0');
  }

  toReadableTime(withAmPm = true, withSecond = false) {
    if (withAmPm) {
      // 오전 오후

      const amPm = this.hour < 12 ? 'a' : 'p';
      const h = this.hour % 12 === 0 ? '12' : this.hour % 12;

      return `${amPm}${h}:${this.minute.toString().padStart(2, '0')} ${
        withSecond ? `${this.second}초` : ''
      }`;
    }

    return '';
  }
}
export { MyDate, YearMonth, PeriodYM, MyTime };
